/**
 * iter92 — FactureSendActions
 *
 * Ré-envoi d'une facture DÉJÀ ÉMISE et non soldée (FactureDetail) :
 *   - Mail (saisie email si manquant → sauvegardé sur la fiche client)
 *   - Courrier (téléchargement PDF nommé "<n° facture>.pdf")
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Printer, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { downloadAuthBlob } from "@/lib/downloadPdf";
import EmailComposeModal from "@/components/mail/EmailComposeModal";

// Couleur Tailwind associée à chaque code statut d'email
const STATUT_BG = {
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
  blue: "border-blue-300 bg-blue-50 text-blue-800",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  orange: "border-orange-300 bg-orange-50 text-orange-800",
  red: "border-red-300 bg-red-50 text-red-800",
  gray: "border-gray-300 bg-gray-50 text-gray-700",
};

export default function FactureSendActions({ facture: f, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  if (!f || f.statut === "annule" || !(f.reste_a_payer > 0.01)) return null;

  const post = async (body) => {
    const { data } = await api.post(`/factures/${f.id}/send-action`, body);
    return data || {};
  };

  const openMailModal = () => {
    setEmailModalOpen(true);
  };

  const sendCourrier = async () => {
    setBusy("courrier");
    try {
      await post({ action: "courrier" });
      const url = f.or_id ? `/api/or/${f.or_id}/facture/pdf` : `/api/factures/${f.id}/pdf`;
      await downloadAuthBlob(url, `${f.numero}.pdf`);
      toast.success(`PDF ${f.numero}.pdf téléchargé pour envoi courrier`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur téléchargement");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="p-4 mt-4 border-blue-200 bg-gradient-to-br from-blue-50/60 to-white" data-testid="facture-send-actions">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-gray-700 max-w-xl">
          <strong>Envoyer cette facture au client</strong>
          <span className="block text-xs text-gray-500 mt-0.5">
            Reste à payer : <span className="font-mono font-semibold text-amber-700">{f.reste_a_payer.toFixed(2)} CHF</span>
            {" "}· Courrier = PDF nommé {f.numero}.pdf
          </span>
          {/* iter100e — Badge email (SendGrid webhooks delivered/opened/bounce) */}
          {f.email_status && f.email_status.event && (
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 mt-1.5 rounded border ${STATUT_BG[f.email_status.color] || STATUT_BG.gray}`}
              data-testid="facture-email-status-badge"
              title={[
                f.email_status.to ? `Destinataire : ${f.email_status.to}` : null,
                f.email_status.at ? `Événement : ${new Date(f.email_status.at).toLocaleString("fr-CH")}` : null,
                f.email_status.reason ? `Raison : ${f.email_status.reason}` : null,
              ].filter(Boolean).join("\n")}
            >
              <Mail className="w-3 h-3" />
              Email : {f.email_status.label}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            disabled={!!busy}
            onClick={openMailModal}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="facture-send-mail-btn"
          >
            {busy === "mail" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
            Mail
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!!busy}
            onClick={sendCourrier}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="facture-send-courrier-btn"
          >
            {busy === "courrier" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}
            Courrier
          </Button>
        </div>
      </div>

      <EmailComposeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        kind="facture"
        entityId={f.id}
        entityLabel={`Facture ${f.numero}`}
        onSent={() => onRefresh?.()}
      />
    </Card>
  );
}
